import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, ClipboardCheck, Clock, Target, Sparkles } from "lucide-react";
import { useScrollReveal } from "@/hooks/useScrollReveal";

const MaturityCheckCTA = () => {
  const { ref: sectionRef, isVisible } = useScrollReveal({ threshold: 0.2 });

  return (
    <section 
      ref={sectionRef}
      className="py-16 md:py-20 bg-white relative overflow-hidden"
    >
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div 
          className={`absolute top-10 left-10 w-32 h-32 bg-ch-purple/10 rounded-full blur-3xl transition-all duration-700 ${
            isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-50'
          }`} 
        />
        <div 
          className={`absolute bottom-10 right-10 w-40 h-40 bg-ch-fuchsia/10 rounded-full blur-3xl transition-all duration-700 delay-300 ${
            isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-50'
          }`} 
        />
      </div>

      <div className="container relative">
        <div className="max-w-4xl mx-auto">
          <div 
            className={`bg-white/80 backdrop-blur-sm border border-ch-purple/15 rounded-2xl p-8 md:p-12 shadow-ch-card transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="flex flex-col md:flex-row items-center gap-8">
              {/* Icon */}
              <div 
                className={`shrink-0 transition-all duration-500 delay-200 ${
                  isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
                }`}
              >
                <div className="relative">
                  <div className="absolute inset-0 bg-ch-hero rounded-2xl blur-lg opacity-30" />
                  <div className="relative w-20 h-20 md:w-24 md:h-24 bg-ch-hero rounded-2xl flex items-center justify-center">
                    <ClipboardCheck className="w-10 h-10 md:w-12 md:h-12 text-white" />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 text-center md:text-left space-y-4">
                <div 
                  className={`inline-flex items-center gap-2 rounded-full font-carlito text-xs uppercase tracking-widest text-ch-acai px-4 py-1.5 transition-all duration-500 delay-300 ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                  }`}
                  style={{ backgroundColor: "rgba(166, 241, 255, 0.4)" }}
                >
                  <Sparkles className="w-3.5 h-3.5 text-ch-fuchsia" strokeWidth={2.5} aria-hidden={true} />
                  Free Assessment
                </div>
                
                <h2 
                  className={`text-2xl md:text-3xl font-heading text-ch-purple transition-all duration-500 delay-[400ms] ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                  }`}
                  style={{
                    background: "linear-gradient(to right, #7130A0, #ED1878)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  Where does your compliance system break?
                </h2>
                
                <p 
                  className={`text-ch-acai/75 text-lg transition-all duration-500 delay-500 ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                  }`}
                >
                  Take our 5-minute Compliance Scorecard and get a personalised report showing where your current tools fall short.
                </p>

                {/* Benefits */}
                <div 
                  className={`flex flex-wrap justify-center md:justify-start gap-4 pt-2 transition-all duration-500 delay-[600ms] ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                  }`}
                >
                  <div className="flex items-center gap-2 text-sm text-ch-acai/70">
                    <Clock className="w-4 h-4 text-ch-fuchsia" />
                    <span>5 minutes</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-ch-acai/70">
                    <Target className="w-4 h-4 text-ch-fuchsia" />
                    <span>Personalised results</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-ch-acai/70">
                    <ClipboardCheck className="w-4 h-4 text-ch-fuchsia" />
                    <span>Actionable fixes</span>
                  </div>
                </div>
              </div>

              {/* CTA Button */}
              <div 
                className={`shrink-0 transition-all duration-500 delay-700 ${
                  isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
                }`}
              >
                <Button
                  asChild
                  size="lg"
                  variant="chPrimary"
                  className="group"
                >
                  <Link to="/compliance-scorecard">
                    Start the Check
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MaturityCheckCTA;
